import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    FilePlus,
    FolderPlus,
    FolderTree,
    PanelLeftClose,
    PanelLeftOpen,
    Columns2,
    Plus,
    Settings,
    X,
} from 'lucide-react';
import { TemplateGalleryBody } from '../editor/DocTemplateGallery';
import { SpaceFormBody } from '../settings/DocSpaceForm';

const COLLAPSED_KEY = 'orbit-doc-sidebar-collapsed';

export function readCollapsed() {
    try {
        return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
        return false;
    }
}

export function writeCollapsed(val) {
    localStorage.setItem(COLLAPSED_KEY, val ? '1' : '0');
}

export default function DocSidebarShell({
    isSettings,
    isOpen,
    onClose,
    onCreateSpace,
    currentSpace = null,
    collapsed,
    onCollapse,
    onExpand,
    isPage = false,
    tocPanel = null,
    children,
}) {
    const navigate = useNavigate();

    const [mobileTab, setMobileTab] = useState('tree');
    const [showCreateDropdown, setShowCreateDropdown] = useState(false);
    const [showTemplateGallery, setShowTemplateGallery] = useState(false);
    const [showDirForm, setShowDirForm] = useState(false);
    const createDropdownRef = useRef(null);

    useEffect(() => { setMobileTab('tree'); }, [isPage]);

    useEffect(() => {
        if (!showCreateDropdown) return;
        const handler = (e) => {
            if (createDropdownRef.current && !createDropdownRef.current.contains(e.target)) setShowCreateDropdown(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showCreateDropdown]);

    const handleCreate = useCallback(() => {
        if (currentSpace) {
            setShowCreateDropdown((prev) => !prev);
        } else if (onCreateSpace) {
            onCreateSpace();
        }
    }, [onCreateSpace, currentSpace]);

    /* ── Collapsed view (desktop only) ── */
    if (collapsed && !isOpen) {
        return (
            <aside
                id="doc-sidebar"
                className="hidden md:flex flex-col items-center w-12 bg-stone-50 border-r border-stone-200 py-3 gap-2 flex-shrink-0"
            >
                <button
                    type="button"
                    onClick={onExpand}
                    className="p-2 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-200 transition-colors"
                    aria-label="Ouvrir la sidebar"
                >
                    <PanelLeftOpen size={16} />
                </button>

                <div className="w-6 border-t border-stone-200" />

                {/* Colonne réduite : les mêmes deux remontées, en pictogrammes. */}
                {!isSettings && currentSpace && (
                    <a
                        href="/docs"
                        onClick={(e) => { e.preventDefault(); navigate('/docs'); }}
                        title="Tous les espaces documentaires"
                        aria-label="Revenir à tous les espaces documentaires"
                        className="p-2 rounded-lg text-stone-500 hover:text-stone-900 hover:bg-stone-200 transition-colors"
                    >
                        <ArrowLeft size={16} aria-hidden="true" />
                    </a>
                )}

                {isSettings ? (
                    <Settings size={15} className="text-stone-400" />
                ) : currentSpace ? (
                    <a
                        href={`/docs/space/${currentSpace.id}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/docs/space/${currentSpace.id}`); }}
                        title={`Accueil de l'espace ${currentSpace.name}`}
                        aria-label={`Accueil de l'espace ${currentSpace.name}`}
                        className="p-2 rounded-lg text-stone-500 hover:text-stone-700 hover:bg-stone-200 transition-colors leading-none"
                    >
                        <FolderTree size={15} aria-hidden="true" />
                    </a>
                ) : (
                    <FolderTree size={15} className="text-stone-400" />
                )}
            </aside>
        );
    }

    return (
        <>
            {/* Mobile backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}

            <aside
                id="doc-sidebar"
                role="navigation"
                aria-label="Navigation documentaire"
                className={`
                    fixed top-0 left-0 z-50 h-full w-64
                    bg-stone-50 border-r border-stone-200
                    flex flex-col transition-transform duration-200 ease-in-out
                    md:relative md:translate-x-0 md:z-auto
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
            >
                {/* ── Header ── */}
                <div
                    id="doc-sidebar-header"
                    className="flex items-center justify-between px-4 h-14 border-b border-stone-200 flex-shrink-0"
                >
                    {currentSpace ? (
                        /* Deux remontées, qui manquaient toutes les deux depuis un
                           espace ou une page : la flèche revient à la liste de TOUS
                           les espaces (/docs), le titre à l'accueil de CET espace. */
                        <span className="flex items-center gap-1 min-w-0">
                            <a
                                href="/docs"
                                onClick={(e) => { e.preventDefault(); navigate('/docs'); }}
                                title="Tous les espaces documentaires"
                                aria-label="Revenir à tous les espaces documentaires"
                                className="-ml-1.5 p-1.5 rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-200 transition-colors flex-shrink-0"
                            >
                                <ArrowLeft size={16} aria-hidden="true" />
                            </a>
                            <a
                                href={`/docs/space/${currentSpace.id}`}
                                onClick={(e) => { e.preventDefault(); navigate(`/docs/space/${currentSpace.id}`); }}
                                title={`Accueil de l'espace ${currentSpace.name}`}
                                className="text-sm font-semibold text-stone-900 tracking-tight truncate max-w-[120px] hover:text-blue-600 transition-colors"
                            >
                                {currentSpace.name}
                            </a>
                        </span>
                    ) : (
                        <a
                            href="/docs"
                            onClick={(e) => { e.preventDefault(); navigate('/docs'); }}
                            className="text-sm font-semibold text-stone-900 tracking-tight hover:text-blue-600 transition-colors"
                        >
                            Documentation
                        </a>
                    )}
                    <div className="flex items-center gap-0.5">
                        <button
                            type="button"
                            onClick={onCollapse}
                            title="Réduire la sidebar"
                            aria-label="Réduire la sidebar"
                            className="hidden md:block p-1.5 rounded-md hover:bg-stone-200 text-stone-400 hover:text-stone-600 transition-colors"
                        >
                            <PanelLeftClose size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="md:hidden p-1 rounded-md hover:bg-stone-200 text-stone-500 transition-colors"
                            aria-label="Fermer la sidebar"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* ── Mobile tabs (page document only) ── */}
                {isPage && tocPanel && (
                    <div className="flex border-b border-stone-200 flex-shrink-0 md:hidden">
                        <button
                            type="button"
                            onClick={() => setMobileTab('tree')}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                                mobileTab === 'tree'
                                    ? 'text-blue-600 border-b-2 border-blue-500 bg-blue-50/50'
                                    : 'text-stone-400 hover:text-stone-600'
                            }`}
                        >
                            <FolderTree size={13} />
                            Arborescence
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobileTab('toc')}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                                mobileTab === 'toc'
                                    ? 'text-blue-600 border-b-2 border-blue-500 bg-blue-50/50'
                                    : 'text-stone-400 hover:text-stone-600'
                            }`}
                        >
                            <Columns2 size={13} />
                            Panneau
                        </button>
                    </div>
                )}

                {/* ── Body (contextual children or ToC on mobile) ── */}
                <div id="doc-sidebar-body" className="flex-1 overflow-y-auto doc-sidebar-scroll">
                    {isPage && tocPanel && mobileTab === 'toc' ? tocPanel : children}
                </div>

                {/* ── Pied de colonne : le geste de création ──
                    Il vit SOUS l'arborescence, au bout de la liste qu'il
                    allonge : c'est là que se lit « et un de plus ». À côté du
                    titre, ce « + » passait pour une action sur l'espace
                    lui-même, à égalité avec « réduire la colonne ».
                    Le menu s'ouvre donc VERS LE HAUT (`bottom-full`) — vers le
                    bas, il sortirait de la fenêtre.
                    (« Configuration » et « Corbeille » restent hors de ce pied :
                    elles doublaient l'en-tête de /docs, qui est leur place.) */}
                {!isSettings && (
                    <div ref={createDropdownRef} className="relative flex-shrink-0 border-t border-stone-200 p-2">
                        {showCreateDropdown && currentSpace && (
                            <div className="absolute bottom-full left-2 right-2 mb-1 z-50 bg-white border border-stone-200 rounded-xl shadow-xl py-1">
                                <button
                                    type="button"
                                    onClick={() => { setShowCreateDropdown(false); setShowTemplateGallery(true); }}
                                    className="ctx-btn"
                                >
                                    <FilePlus size={14} className="text-stone-400" /> Nouvelle page
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowCreateDropdown(false); setShowDirForm(true); }}
                                    className="ctx-btn"
                                >
                                    <FolderPlus size={14} className="text-stone-400" /> Nouveau répertoire
                                </button>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={handleCreate}
                            aria-haspopup={currentSpace ? 'menu' : undefined}
                            aria-expanded={currentSpace ? showCreateDropdown : undefined}
                            title={currentSpace ? 'Nouveau contenu' : 'Nouvel espace'}
                            className="w-full inline-flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium text-stone-500 hover:bg-stone-200 hover:text-stone-700 transition-colors"
                        >
                            <Plus size={14} aria-hidden="true" />
                            {currentSpace ? 'Nouveau contenu' : 'Nouvel espace'}
                        </button>
                    </div>
                )}
            </aside>

            {showTemplateGallery && currentSpace && createPortal(
                <TemplateGalleryBody
                    isOpen
                    directoryId={currentSpace.id}
                    onClose={() => setShowTemplateGallery(false)}
                />,
                document.body,
            )}

            {showDirForm && currentSpace && createPortal(
                <SpaceFormBody
                    isOpen
                    parentId={currentSpace.id}
                    parentName={currentSpace.name}
                    onClose={() => setShowDirForm(false)}
                />,
                document.body,
            )}
        </>
    );
}
